import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { MONTHS, WEEKDAYS, fa, isoToJ, jToIso, monthGrid, monthRange, todayIso, todayJ } from "../../core/format";
import Select from "../../components/Select";

// همه‌ی شیفت‌ها ۲۴ ساعته‌اند؛ برای سازگاری با دیتابیس/سرور همان مقدار قدیمی ذخیره می‌شود
const FULL = "morning";
const FULL_LABEL = "شیفت ۲۴ ساعته";
const MONTHS_AHEAD = 3; // تعداد ماه‌های خلاصه‌ی شیفت (ماه جاری + ماه‌های بعد)

const parseDays = (txt, max) => {
  const out = new Set();
  const t = String(txt).replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d)).replace(/[،؛]/g, ",");
  for (const part of t.split(/[,\s]+/).filter(Boolean)) {
    const m = part.match(/^(\d{1,2})(?:-(\d{1,2}))?$/);
    if (!m) return null;
    const a = Number(m[1]);
    const b = m[2] ? Number(m[2]) : a;
    if (a < 1 || b > max || a > b) return null;
    for (let i = a; i <= b; i++) out.add(i);
  }
  return out;
};

export default function Shifts() {
  const { toast, confirm } = useApp();
  const [emps, setEmps] = useState([]);
  const [empId, setEmpId] = useState("");
  const tj = todayJ();
  const [ym, setYm] = useState({ y: tj.jy, m: tj.jm });
  const [all, setAll] = useState([]); // شیفت‌های ذخیره‌شده‌ی همه‌ی کارکنان در این ماه
  const [entries, setEntries] = useState({}); // iso -> true (روزهای شیفت)
  const [dirty, setDirty] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState([]); // شیفت‌های ۳ ماه آینده (برای خلاصه)

  const { days, offset } = monthGrid(ym.y, ym.m);
  const range = useMemo(() => monthRange(ym.y, ym.m), [ym]);

  // بازه‌ی خلاصه: از امروز تا پایان ماه سوم (ماه جاری + ۲ ماه بعد)
  const upRange = useMemo(() => {
    let y = tj.jy, m = tj.jm + MONTHS_AHEAD - 1;
    while (m > 12) { m -= 12; y++; }
    return { from: todayIso(), to: monthRange(y, m).to, lastY: y, lastM: m };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tj.jy, tj.jm]);

  const loadAll = useCallback(async () => {
    try { const { data } = await api.get("/shifts", { params: range }); setAll(data.items); }
    catch (e) { toast(errMsg(e), "err"); }
  }, [range, toast]);

  const loadUpcoming = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/shifts", { params: { from: upRange.from, to: upRange.to } }); setUpcoming(data.items); }
    catch (e) { toast(errMsg(e), "err"); } finally { setLoading(false); }
  }, [upRange, toast]);

  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data.items)).catch((e) => toast(errMsg(e), "err")); }, [toast]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadUpcoming(); }, [loadUpcoming]);

  // روزهای ویرایش از روی داده‌ی ذخیره‌شده‌ی کارمند انتخاب‌شده
  useEffect(() => {
    const o = {};
    all.filter((s) => String(s.employeeId) === String(empId)).forEach((s) => { o[s.shiftDate] = true; });
    setEntries(o); setDirty(false);
  }, [all, empId]);

  const guard = async () => (!dirty || (await confirm("تغییرات ذخیره‌نشده از بین می‌رود. ادامه می‌دهید؟", { okText: "ادامه", danger: false })));

  const changeEmp = async (v) => { if (await guard()) setEmpId(v); };
  const move = async (delta) => {
    if (!(await guard())) return;
    let m = ym.m + delta, y = ym.y;
    if (m > 12) { m = 1; y++; } if (m < 1) { m = 12; y--; }
    setYm({ y, m });
  };

  const toggle = (d) => {
    if (!empId) return toast("ابتدا یک کارمند را انتخاب کنید", "err");
    const iso = jToIso(ym.y, ym.m, d);
    setEntries((e) => { const n = { ...e }; if (n[iso]) delete n[iso]; else n[iso] = true; return n; });
    setDirty(true);
  };

  const applyText = () => {
    if (!empId) return toast("ابتدا یک کارمند را انتخاب کنید", "err");
    const set = parseDays(text, days);
    if (!set || !set.size) return toast(`روزها را مثل «1,2,5,10-14,${days}» وارد کنید`, "err");
    setEntries((e) => { const n = { ...e }; set.forEach((d) => { n[jToIso(ym.y, ym.m, d)] = true; }); return n; });
    setDirty(true); setText("");
  };

  const fillAll = () => {
    if (!empId) return toast("ابتدا یک کارمند را انتخاب کنید", "err");
    const n = {}; for (let d = 1; d <= days; d++) n[jToIso(ym.y, ym.m, d)] = true;
    setEntries(n); setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/shifts/employee/${empId}`, {
        ...range,
        entries: Object.keys(entries).map((date) => ({ date, shiftType: FULL })),
      });
      toast("برنامه شیفت ذخیره شد");
      await Promise.all([loadAll(), loadUpcoming()]);
    } catch (e) { toast(errMsg(e), "err"); } finally { setSaving(false); }
  };

  const today = todayIso();
  const empName = (e) => `${e.firstName} ${e.lastName}`;
  const byEmp = useMemo(() => {
    const m = {};
    [...upcoming].sort((a, b) => (a.shiftDate < b.shiftDate ? -1 : 1)).forEach((s) => { (m[s.employeeId] ||= []).push(s); });
    return m;
  }, [upcoming]);

  return (
    <>
      <div className="page-head"><div><h2>برنامه شیفت کاری</h2><p>همه‌ی شیفت‌ها ۲۴ ساعته‌اند؛ روزهای شیفت هر پرسنل را از روی تقویم انتخاب کنید</p></div></div>

      <div className="card card-pad">
        <div className="toolbar">
          <div className="field" style={{ maxWidth: 300 }}>
            <label>کارمند</label>
            <Select value={empId} onChange={changeEmp} searchable placeholder="انتخاب کارمند..."
              options={emps.map((e) => ({ value: e.id, label: empName(e) }))} />
          </div>
          <div className="field" style={{ maxWidth: 260 }}>
            <label>ماه</label>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button className="icon-btn" onClick={() => move(1)} aria-label="ماه بعد"><i className="fa-solid fa-chevron-right" /></button>
              <div style={{ flex: 1, textAlign: "center", fontWeight: 800, color: "var(--primary)" }}>{MONTHS[ym.m - 1]} {fa(ym.y)}</div>
              <button className="icon-btn" onClick={() => move(-1)} aria-label="ماه قبل"><i className="fa-solid fa-chevron-left" /></button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, fontSize: 13 }}>
          <span className="dot night" />
          <span style={{ fontWeight: 700 }}>روزهای رنگی = {FULL_LABEL}</span>
          <span className="hint" style={{ margin: 0 }}>(برای انتخاب یا لغو، روی روز کلیک کنید)</span>
        </div>

        <div className="cal" style={{ opacity: empId ? 1 : .55 }}>
          {WEEKDAYS.map((w) => <div key={w} className="cal-h">{w}</div>)}
          {Array.from({ length: offset }, (_, i) => <div key={`b${i}`} className="cal-d blank" />)}
          {Array.from({ length: days }, (_, i) => {
            const d = i + 1;
            const iso = jToIso(ym.y, ym.m, d);
            const on = !!entries[iso];
            return (
              <button key={d} type="button" className={`cal-d ${on ? "night" : ""} ${iso === today ? "today" : ""}`} onClick={() => toggle(d)}>
                {fa(d)}
                {on && <small>۲۴ ساعته</small>}
              </button>
            );
          })}
        </div>

        <div className="toolbar" style={{ marginTop: 16 }}>
          <div className="field" style={{ maxWidth: 320 }}>
            <label>انتخاب سریع روزها (با کاما یا بازه)</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="ltr" placeholder={`1,2,5,10-14,${days}`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyText()} />
              <button className="btn btn-ghost" onClick={applyText}>اعمال</button>
            </div>
          </div>
          <button className="btn btn-ghost" onClick={fillAll}>همه‌ی روزهای ماه</button>
          <button className="btn btn-ghost" onClick={() => { setEntries({}); setDirty(true); }}>پاک کردن همه</button>
          <button className="btn" onClick={save} disabled={!empId || !dirty || saving} style={{ marginInlineStart: "auto" }}>
            {saving ? "در حال ذخیره..." : `ذخیره (${fa(Object.keys(entries).length)} روز)`}
          </button>
        </div>
        {empId && dirty && <div className="alert info">تغییرات هنوز ذخیره نشده است.</div>}
      </div>

      <div className="page-head" style={{ marginTop: 22 }}><h2 style={{ fontSize: 16 }}>برنامه شیفت {fa(MONTHS_AHEAD)} ماه آینده (از امروز تا پایان {MONTHS[upRange.lastM - 1]} {fa(upRange.lastY)})</h2></div>
      <div className="card">
        {loading ? <div className="empty"><div className="spinner" style={{ margin: "0 auto" }} /></div> : (
          <div className="table-wrap">
            <table className="tbl">
              <thead><tr><th>کارمند</th><th>روزهای شیفت ({FULL_LABEL}) — روز، ماه و سال</th><th style={{ width: 1 }} /></tr></thead>
              <tbody>
                {emps.map((e) => {
                  const list = byEmp[e.id] || [];
                  const groups = [];
                  list.forEach((s) => {
                    const j = isoToJ(s.shiftDate);
                    const key = `${j.jy}-${j.jm}`;
                    let g = groups.find((x) => x.key === key);
                    if (!g) { g = { key, jy: j.jy, jm: j.jm, days: [] }; groups.push(g); }
                    if (!g.days.includes(j.jd)) g.days.push(j.jd);
                  });
                  return (
                    <tr key={e.id}>
                      <td><b>{empName(e)}</b></td>
                      <td>
                        {groups.length === 0 ? <span style={{ color: "var(--muted)" }}>—</span> : (
                          <div className="chips">
                            {groups.map((g) => (
                              <span className="chip" key={g.key}>
                                <span className="dot night" />
                                {MONTHS[g.jm - 1]} {fa(g.jy)}: {g.days.map((d) => fa(d)).join("، ")}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td><button className="icon-btn" title="ویرایش" onClick={() => changeEmp(String(e.id))}><i className="fa-solid fa-pen" /></button></td>
                    </tr>
                  );
                })}
                {emps.length === 0 && <tr><td colSpan="3"><div className="empty">ابتدا از بخش «کارکنان» پرسنل را ثبت کنید.</div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}